import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UserButton } from "@clerk/react";
import { Plus, CloudArrowUp, Paperclip } from "@phosphor-icons/react";
import { useApi, errMsg, nzDate, nzDateTime } from "@/lib/api";
import { Btn, Section, Empty, Field, Pill, SaveState, useLoad, useUnsavedGuard } from "@/components/app/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { WeightChart, StockSummary, AnimalTable, CommentThread } from "@/components/grazing/shared";
import { IntakeSections } from "@/components/grazing/IntakeForm";
import { FormStatus } from "@/components/grazing/FormsTab";

function MobView({ mobId }) {
  const api = useApi();
  const [d, , err] = useLoad(() => api.get(`/portal/mobs/${mobId}/stock`), [mobId]);
  if (err) return <p className="text-sm text-red-700">{err}</p>;
  if (!d) return <p className="text-sm">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="portal-mob-view">
      <StockSummary summary={d.summary} />
      <Section title="Weight gain" testid="portal-weight-section"><WeightChart sessions={d.sessions} testid="portal-weight-chart" /></Section>
      <Section title="Animals" testid="portal-animals"><AnimalTable base="/portal" animals={d.animals} /></Section>
      <CommentThread base="/portal" mobId={mobId} animals={d.animals} />
    </div>
  );
}

function StockTab({ client }) {
  const mobs = client.mobs;
  const [sel, setSel] = useState(mobs.find((m) => m.status === "active")?.id || mobs[0]?.id);
  if (!mobs.length) return <Empty testid="portal-no-mobs">No mobs yet. Once your stock arrives at {client.farm_name} it will appear here.</Empty>;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="portal-mob-list">{mobs.map((m) => (
        <button key={m.id} onClick={() => setSel(m.id)} data-testid={`portal-mob-${m.id}`} className={`panel px-4 py-3 text-left transition-colors ${sel === m.id ? "ring-2 ring-emerald-700" : "hover:bg-slate-50"}`}>
          <p className="font-semibold text-sm">{m.name}</p><p className="text-xs text-slate-500">{m.status === "active" ? `${m.active_animals} active · arrived ${nzDate(m.arrival_date)}` : `Departed ${nzDate(m.departure_date)}`}</p></button>))}</div>
      {sel && <MobView key={sel} mobId={sel} />}
    </div>
  );
}

function Uploader({ clientId, onUploaded }) {
  const api = useApi();
  const ref = useRef();
  const [busy, setBusy] = useState(null);
  const up = async (file) => {
    setBusy(file.name);
    try { const fd = new FormData(); fd.append("client_id", clientId); fd.append("file", file); onUploaded(await api.post("/portal/files", fd, { onUploadProgress: (e) => setBusy(`${file.name} ${Math.round((e.loaded / (e.total || 1)) * 100)}%`) })); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (<><input ref={ref} type="file" className="hidden" accept="image/*,application/pdf,.csv,.docx,.xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) up(f); e.target.value = ""; }} data-testid="portal-file-input" />
    <Btn type="button" variant="outline" onClick={() => ref.current.click()} disabled={!!busy} data-testid="portal-file-button"><CloudArrowUp size={18} />{busy ? `Uploading ${busy}` : "Attach certificate / record"}</Btn></>);
}

function FileList({ files, onRemove }) {
  const api = useApi();
  const open = async (f) => { try { window.open(URL.createObjectURL(await api.blob(`/portal/files/${f.id}`)), "_blank"); } catch (e) { toast.error(errMsg(e)); } };
  return <ul className="flex flex-wrap gap-2 mt-2">{files.map((f) => <li key={f.id} className="inline-flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
    <button type="button" onClick={() => open(f)} className="inline-flex items-center gap-1 hover:underline" data-testid={`portal-file-${f.id}`}><Paperclip size={12} />{f.filename}</button>
    {onRemove && <button type="button" onClick={() => onRemove(f.id)} className="text-red-600">remove</button>}</li>)}</ul>;
}

function FormEditor({ id, onBack }) {
  const api = useApi();
  const [sub, setSub] = useState(null);
  const [state, setState] = useState(null);
  useUnsavedGuard(state === "dirty");
  useEffect(() => { api.get(`/portal/intake/${id}`).then(setSub).catch((e) => toast.error(errMsg(e))); }, [id, api]);
  if (!sub) return <p className="text-sm">Loading…</p>;
  const draft = sub.status === "draft";
  const upd = (patch) => { setSub({ ...sub, ...patch }); setState("dirty"); };
  const payload = () => ({ answers: sub.answers, attachments: sub.attachment_files.map((f) => f.id), declaration_name: sub.declaration_name });
  const save = async () => { setState("saving"); try { setSub(await api.patch(`/portal/intake/${id}`, payload())); setState("saved"); } catch (e) { setState("failed"); toast.error(errMsg(e)); } };
  const submit = async () => {
    try { await api.patch(`/portal/intake/${id}`, payload()); const r = await api.post(`/portal/intake/${id}/submit`); setSub(r); setState(null); toast.success("Form submitted. The farm team has been notified."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const t = sub.template;
  return (
    <div className="space-y-4 min-w-0" data-testid="portal-form-editor">
      <button className="text-sm underline" onClick={onBack} data-testid="portal-form-back">← My forms</button>
      <Section title={t.title}><p className="text-sm text-slate-600">{t.intro}</p><div className="mt-2"><FormStatus s={sub.status} /></div></Section>
      <IntakeSections sections={t.sections} answers={sub.answers} onChange={(a) => upd({ answers: a })} readOnly={!draft} />
      <Section title="Certificates & records" testid="portal-attachments">
        {draft && <Uploader clientId={sub.client_id} onUploaded={(f) => upd({ attachment_files: [...sub.attachment_files, f] })} />}
        {sub.attachment_files.length ? <FileList files={sub.attachment_files} onRemove={draft ? (fid) => upd({ attachment_files: sub.attachment_files.filter((f) => f.id !== fid) }) : null} /> : !draft && <p className="text-sm text-slate-500">No files attached.</p>}
      </Section>
      <Section title="Owner declaration" testid="portal-declaration">
        <p className="text-sm">{t.declaration}</p>
        {draft ? <div className="mt-3 max-w-sm"><Field label="Type your full name to sign"><input className="field-input" value={sub.declaration_name} onChange={(e) => upd({ declaration_name: e.target.value })} data-testid="portal-declaration-name" /></Field></div>
          : <p className="text-sm font-semibold mt-3">Signed: {sub.declaration_name} · {nzDate(sub.declaration_date)}</p>}
      </Section>
      {t.footer && <p className="text-xs text-slate-500">{t.footer}</p>}
      {draft && <div className="flex flex-wrap justify-end gap-2 items-center sticky bottom-0 bg-[#fbfaf6] py-3"><SaveState state={state} onRetry={save} />
        <Btn variant="outline" onClick={save} data-testid="portal-form-save">Save draft</Btn><Btn onClick={submit} data-testid="portal-form-submit">Submit to farm</Btn></div>}
    </div>
  );
}

function FormsTab({ client }) {
  const api = useApi();
  const [forms, reload] = useLoad(() => api.get("/portal/intake"), []);
  const [open, setOpen] = useState(null);
  if (open) return <FormEditor id={open} onBack={() => { setOpen(null); reload(); }} />;
  const start = async () => { try { setOpen((await api.post("/portal/intake", { client_id: client.id })).id); } catch (e) { toast.error(errMsg(e)); } };
  const discard = async (f) => { if (!window.confirm("Delete this draft form?")) return; try { await api.del(`/portal/intake/${f.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const mine = (forms || []).filter((f) => f.client_id === client.id);
  return (
    <Section title="Incoming animal forms" testid="portal-forms" actions={<Btn onClick={start} data-testid="portal-new-form"><Plus size={16} />New form</Btn>}>
      <p className="text-xs text-slate-500 mb-3">Fill in one form per mob before it arrives, so the farm has the health and arrival information it needs.</p>
      {mine.length === 0 ? <Empty testid="portal-forms-empty">No forms yet.</Empty> : <ul className="divide-y divide-slate-100">{mine.map((f) => (
        <li key={f.id} className="flex items-center gap-2"><button className="flex-1 text-left py-3 flex flex-wrap justify-between gap-2 hover:bg-slate-50 px-1" onClick={() => setOpen(f.id)} data-testid={`portal-form-${f.id}`}>
          <span className="text-sm"><span className="font-medium">{f.answers?.head_count || "?"} head · arriving {nzDate(f.answers?.arrival_date)}</span><span className="block text-xs text-slate-500">{f.submitted_at ? `Submitted ${nzDateTime(f.submitted_at)}` : `Started ${nzDateTime(f.created_at)}`}</span></span>
          <FormStatus s={f.status} /></button>{f.status === "draft" && <button className="text-xs text-red-600 underline" onClick={() => discard(f)} data-testid={`portal-form-delete-${f.id}`}>Delete</button>}</li>))}</ul>}
    </Section>
  );
}

export default function GrazierPortal() {
  const api = useApi();
  const [d, , err] = useLoad(() => api.get("/portal/me"), []);
  const [cid, setCid] = useState(null);
  const client = d?.clients.find((c) => c.id === cid) || d?.clients[0];
  return (
    <div className="min-h-screen bg-[#fbfaf6]" data-testid="grazier-portal">
      <header className="bg-[#1f2a1c] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex justify-between items-center gap-3">
          <div><p className="text-xs uppercase tracking-[0.25em] text-emerald-200">TCG Grazing</p><p className="font-display text-xl font-bold" data-testid="portal-greeting">Kia ora{d?.user ? `, ${d.user.first_name || (d.user.name || "").split(" ")[0]}` : ""}</p></div>
          <div className="flex items-center gap-3">{d?.clients.length > 1 && <select className="h-10 rounded-md text-slate-900 px-2 text-sm" value={client?.id} onChange={(e) => setCid(e.target.value)} data-testid="portal-client-switcher">{d.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}<UserButton /></div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {err ? <p className="text-sm text-red-700">{err}</p> : !d ? <p className="text-sm">Loading…</p> : !client ? (
          <div className="panel p-8 max-w-lg" data-testid="portal-no-access"><h1 className="text-xl font-bold mb-2">Nearly there</h1><p className="text-sm text-slate-600">Your login isn't linked to any stock yet. Ask the farm manager to add you as a grazier.</p></div>) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="portal-client-name">{client.name}</h1>
            <p className="text-sm text-slate-600 mt-1 mb-6">Grazing at {client.farm_name} <Pill tone="green">{client.mobs.filter((m) => m.status === "active").length} active mob(s)</Pill></p>
            <Tabs defaultValue="stock" key={client.id}>
              <TabsList className="mb-6"><TabsTrigger value="stock" data-testid="portal-tab-stock">My stock</TabsTrigger><TabsTrigger value="forms" data-testid="portal-tab-forms">Incoming animal forms</TabsTrigger></TabsList>
              <TabsContent value="stock"><StockTab client={client} /></TabsContent>
              <TabsContent value="forms"><FormsTab client={client} /></TabsContent>
            </Tabs>
          </>)}
      </main>
    </div>
  );
}
